import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Skeleton } from '@bliks-io/ui';

/**
 * Values Calendly can prefill on the booking form (invitee + guests). Calendly
 * accepts more (`name`, `customAnswers`, …); only the fields this app actually
 * passes are modelled, so an unused option can't drift out of sync unnoticed.
 */
export interface CalendlyPrefill {
  email?: string;
  firstName?: string;
  lastName?: string;
  /** Additional attendee emails (e.g. the logged-in rep). */
  guests?: string[];
}

/** Tracking fields — surface in the Calendly webhook under `payload.tracking`. */
export interface CalendlyUtm {
  /** The submitting partner member's HubSpot contact id, for lead attribution. */
  salesforceUuid?: string;
}

/** The `calendly.event_scheduled` payload (only the bits we consume). */
export interface CalendlyScheduledPayload {
  event?: { uri?: string };
  invitee?: { uri?: string };
}

interface CalendlyGlobal {
  initInlineWidget: (opts: {
    url: string;
    parentElement: HTMLElement;
    prefill?: CalendlyPrefill;
    utm?: CalendlyUtm;
    /** Auto-resize the iframe to its content height (no internal scrollbar). */
    resize?: boolean;
  }) => void;
}

interface CalendlyMessage {
  event?: string;
  payload?: CalendlyScheduledPayload;
}

const WIDGET_SRC = 'https://assets.calendly.com/assets/external/widget.js';
let scriptPromise: Promise<void> | null = null;

/**
 * Load the Calendly widget script once, shared across mounts. A failed load
 * clears the memo (and the dead <script> tag) so the next attempt retries
 * instead of replaying the cached rejection forever.
 */
function loadCalendlyScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_SRC}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = WIDGET_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      scriptPromise = null;
      reject(new Error('Failed to load the Calendly widget'));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Strict origin check for Calendly postMessages. A substring test would let
 * e.g. https://calendly.com.evil.example through — and the scheduled event
 * auto-submits a lead, so only exact calendly.com (sub)domains may pass.
 */
function isCalendlyOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === 'calendly.com' || host.endsWith('.calendly.com');
  } catch {
    return false;
  }
}

interface CalendlyInlineProps {
  url: string;
  prefill?: CalendlyPrefill;
  utm?: CalendlyUtm;
  /** Fired when the invitee confirms a slot (a real Calendly event is created). */
  onScheduled?: (payload: CalendlyScheduledPayload) => void;
  className?: string;
}

/**
 * Inline Calendly booking widget. Loads the Calendly script, renders the date
 * picker prefilled with the given invitee/guests + UTM, and reports the booked
 * event via `onScheduled`. External integration (third-party script + window
 * message subscription), so `useEffect` is the right tool here.
 */
export function CalendlyInline({ url, prefill, utm, onScheduled, className }: CalendlyInlineProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [widgetReady, setWidgetReady] = useState(false);
  // Keep the latest callback/prefill without re-initialising the widget. Written
  // in an effect, never during render: under StrictMode/concurrent rendering a
  // render can be thrown away, and a ref written there would keep the discarded
  // value — here the booking callback, which creates the lead.
  const onScheduledRef = useRef(onScheduled);
  const prefillRef = useRef(prefill);
  const utmRef = useRef(utm);
  useEffect(function trackLatestProps() {
    onScheduledRef.current = onScheduled;
    prefillRef.current = prefill;
    utmRef.current = utm;
  });

  // Init the widget (re-init only when the event URL changes or on retry).
  useEffect(
    function initCalendlyWidget() {
      let cancelled = false;
      loadCalendlyScript()
        .then(() => {
          if (cancelled || !containerRef.current) return;
          const calendly = (window as unknown as { Calendly?: CalendlyGlobal }).Calendly;
          // A blocker can serve widget.js as a no-op script that still fires `load`.
          if (!calendly) {
            setLoadFailed(true);
            return;
          }
          containerRef.current.innerHTML = '';
          calendly.initInlineWidget({
            url,
            parentElement: containerRef.current,
            prefill: prefillRef.current,
            utm: utmRef.current,
            // Auto-fit the iframe to content → no internal Calendly scrollbar.
            resize: true
          });
          // widget.js appends the iframe synchronously, but the box stays blank
          // until the iframe's own document has loaded. Without an iframe there
          // is nothing to wait for, and the skeleton must not hide the widget.
          const iframe = containerRef.current.querySelector('iframe');
          if (!iframe) {
            setWidgetReady(true);
            return;
          }
          iframe.addEventListener(
            'load',
            () => {
              if (!cancelled) setWidgetReady(true);
            },
            { once: true }
          );
        })
        .catch(() => {
          if (!cancelled) setLoadFailed(true);
        });
      return () => {
        cancelled = true;
        // A new URL or a retry re-inits the widget, which starts out blank again.
        setWidgetReady(false);
      };
    },
    [url, loadAttempt]
  );

  // Listen for the booking confirmation.
  useEffect(function subscribeToBookingConfirmation() {
    const handler = (event: MessageEvent<CalendlyMessage>) => {
      if (event.data?.event === 'calendly.event_scheduled' && isCalendlyOrigin(event.origin)) {
        onScheduledRef.current?.(event.data.payload ?? {});
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  if (loadFailed) {
    return (
      <div className={className}>
        <div className="flex h-full flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">{t('calendly.loadError')}</p>
          <Button
            variant="outline"
            onClick={() => {
              setLoadFailed(false);
              setLoadAttempt((attempt) => attempt + 1);
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" aria-busy={!widgetReady}>
      {/* The init wipes the container's innerHTML, so the skeleton is its sibling. */}
      <div ref={containerRef} className={className} />
      {!widgetReady && <Skeleton className="absolute inset-0" />}
    </div>
  );
}
